import { LockOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { useCounterparty } from '@api/useCounterparty'
import { useSites } from '@api/useSites'
import { useUsers } from '@api/useUsers'
import { ManyTag } from '@components/_SiteWide/CellTemplates/ManyTag'
import { PWModalContent } from '@components/DriverManagement/Manage/Form/PWModalContent'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { defaultDateDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'

import {
  BBDTag,
  GraviButton,
  GraviGrid,
  Horizontal,
  NotificationMessage,
  SelectEditor,
  TextCell,
  Texto,
  validateNotEmptyString,
} from '@gravitate-js/excalibrr'
import { sortNumeric } from '@utils/general'
import { Button, Drawer, Modal, Select } from 'antd'
import moment from 'moment'
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { CreateUserForm } from './CreateUserForm'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { useUserSettings } from '@hooks/settings'
import { useMarkets } from '@api/useMarkets'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'users'

export function User() {
  const [creatingUser, setCreatingUser] = useState(false)
  const [viewingPWModal, setViewingPWModal] = useState(false)
  const { useMarketsQuery } = useMarkets()

  const { useUsersQuery, useRolesMetaQuery, updateUserMutation, sendResetEmailRequest } = useUsers()
  const { useCarrierNamesQuery } = useCounterparty()
  const { data: carrierNames } = useCarrierNamesQuery()
  const { data: userData, status } = useUsersQuery()
  const { data: roleMetadata } = useRolesMetaQuery()
  const { data: marketData } = useMarketsQuery()
  const { useSiteListQuery } = useSites()
  const { data: siteList } = useSiteListQuery()

  const marketSelectOptions = useMemo(() => {
    if (!marketData?.rows) return []
    return [...marketData.rows, { name: 'All Markets', id: '' }]
      .map((market) => ({
        label: market.name,
        value: market.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [marketData?.rows])

  const fixAndSaveUser = (user) => {
    delete user.carrier_id
    if (user.carrier === 'No Carrier') {
      user.carrier = null
    }

    user.store_ids = []
    if (!user.carrier && user.roles.some((role) => roleMetadata.carrier_roles.includes(role))) {
      user.roles = user.roles.filter((role) => !roleMetadata.carrier_roles.includes(role))
    } else if (user.carrier) {
      user.roles = user.roles.filter((role) => roleMetadata.carrier_roles.includes(role))
    }
    if (user.market_ids?.[0] === '') {
      user.market_ids = []
    }
    if (user.store_numbers?.[0] === 'All Sites') {
      user.store_numbers = []
    }
    updateUserMutation.mutate(user)
  }

  const { scopes } = useSecurityContext()

  const sendResetEmail = (row) => {
    const { username, email } = row
    if (!email) {
      NotificationMessage(
        'User Email Missing',
        `Unable to send password reset for user. Please add an email first.`,
        true
      )
    } else {
      try {
        sendResetEmailRequest.mutate(username)
        NotificationMessage('Password Reset Email Sent', `A password reset email has been sent to ${email}`, false)
      } catch (e) {
        console.error(e)
      }
    }
  }

  const selectOptions = {
    carrierNames,
    roles: roleMetadata?.roles,
    siteList,
  }
  const { updateSetting } = useUserSettings()
  const columnDefs = useMemo(
    () =>
      getColumnDefs(
        selectOptions,
        scopes,
        sendResetEmail,
        viewingPWModal,
        setViewingPWModal,
        marketData,
        marketSelectOptions
      ),
    [selectOptions, scopes, viewingPWModal, marketData, marketSelectOptions]
  )
  const getRowId = useCallback((row) => row?.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({
      getRowHeight: (params) => (params.data?.roles?.length > 1 ? 70 : 50),
      frameworkComponents: {
        textCell: TextCell,
        select: SelectEditor,
      },
      getRowId,
    }),
    []
  )
  return (
    <>
      <GraviGrid
        controlBarProps={{
          title: 'User Management',
          actionButtons: (
            <GraviButton
              icon={<PlusCircleOutlined />}
              buttonText='Create User'
              theme2
              onClick={() => setCreatingUser(!creatingUser)}
            />
          ),
        }}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        storageKey={STORAGE_KEY}
        rowData={userData?.rows}
        updateEP={fixAndSaveUser}
        loading={status !== 'success' && status !== 'error'}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />

      <Drawer
        title={
          <div className='flex justify-sb'>
            <Texto category='h6'>Create New User</Texto>
          </div>
        }
        placement='right'
        width={500}
        destroyOnClose
        onClose={() => setCreatingUser(!creatingUser)}
        visible={creatingUser}
      >
        <CreateUserForm
          siteList={siteList}
          carrierOptions={carrierNames}
          setCreatingUser={setCreatingUser}
          creatingUser={creatingUser}
        />
      </Drawer>
    </>
  )
}

const getColumnDefs = (
  editableSelectOptions,
  scopes,
  sendResetEmail,
  viewingPWModal,
  setViewingPWModal,
  marketData,
  marketSelectOptions
) => {
  const canAssignRoles = scopes.admin.role_assign || scopes.scopes.includes('ra')
  const canAssignCarriers = scopes.admin.carrier_assign || scopes.scopes.includes('ca')
  const canCreateDrivers = scopes.admin.carrier_driver_create || scopes.scopes.includes('cdc')

  return [
    {
      headerName: 'Active',
      field: 'disabled',
      minWidth: 100,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorPopup: true,
      valueGetter: ({ data }) => (data?.disabled === true ? 'Disabled' : 'Active'),
      cellRenderer: ({ value }) => (
        <BBDTag theme2={value === 'Active'} error={value === 'Disabled'}>
          {value}
        </BBDTag>
      ),
      cellEditorParams: {
        values: [true, false],
        cellHeight: 50,
        formatValue: (params) => (params === true ? 'Disabled' : 'Active'),
      },
    },
    {
      field: 'full_name',
      headerName: 'Full Name',
      comparator: (valueA, valueB) => {
        return (valueA || '')?.toLowerCase().localeCompare((valueB || '')?.toLowerCase())
      },

      filter: true,
      flex: 2.5,
      // cellRenderer: ({ value }) => <TextCell value={value} weight='bold' />,
      valueSetter: validateNotEmptyString,
    },
    {
      field: 'username',
      filter: true,
      flex: 2.5,
      editable: false,
      sort: 'asc',
      comparator: (valueA, valueB) => {
        return valueA?.toLowerCase().localeCompare(valueB?.toLowerCase())
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 200,
      valueSetter: validateNotEmptyString,
    },
    {
      field: 'carrier',
      filter: true,
      editable: canAssignCarriers,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorPopup: true,
      minWidth: 200,
      hide: !canAssignCarriers,
      required: false,
      cellEditorParams: {
        values: editableSelectOptions?.carrierNames && ['No Carrier', ...editableSelectOptions.carrierNames],
      },
    },
    {
      field: 'store_numbers',
      headerName: 'Assigned Sites',
      minWidth: 200,
      autoHeight: true,
      cellRenderer: ({ value }) => <ManyTag tagItems={!value?.length ? ['All Sites'] : value} maxCount={2} />,
      cellEditor: SelectEditor,
      filter: true,
      cellEditorParams: {
        options: ['All Sites', ...sortNumeric(editableSelectOptions?.siteList?.map((site) => site.store_number) || [])],
        placeholder: 'Assign Site',
        mode: 'multiple',
      },
      flex: 2,
    },
    {
      field: 'roles',
      headerName: 'User Roles',
      // cellEditorPopup: true,
      cellRenderer: ({ value }) => <ManyTag tagItems={value} maxCount={2} />,
      cellEditor: 'select',
      filter: true,
      cellEditorParams: {
        options: editableSelectOptions && editableSelectOptions?.roles,
        placeholder: 'Select Roles',
        mode: 'multiple',
      },
      minWidth: 200,
      hide: !canAssignRoles,
      editable: canAssignRoles,
    },
    {
      field: 'created_date',
      ...defaultDateDef('created_date'),
      headerName: 'Created Date',
      editable: false,
      flex: 2,
      cellRenderer: ({ value }) => formatters.DATE(moment(value)),
    },
    {
      headerName: 'Is Driver',
      field: 'is_driver',
      minWidth: 100,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorPopup: true,
      cellRenderer: ({ value }) => <BBDTag theme2={value}>{value ? 'Yes' : 'No'}</BBDTag>,
      cellEditorParams: {
        values: [true, false],
        cellHeight: 50,
        formatValue: (params) => (params === true ? 'Yes' : 'No'),
      },
      hide: !canCreateDrivers,
      editable: canCreateDrivers,
    },
    {
      headerName: 'Markets',
      field: 'market_ids',
      minWidth: 250,
      cellEditor: CustomSelectEditor,
      cellEditorParams: {
        options: marketSelectOptions,
      },
      cellRenderer: (params) => {
        const displayValue = params.data.market_ids?.length
          ? params.data.market_ids.map((market) => marketData.rows.find((m) => m.id === market)?.name)
          : ['All Markets']
        return <ManyTag tagItems={displayValue} maxCount={3} />
      },
      filterParams: {
        valueGetter: (params) => {
          if (!params.data.market_ids?.length) return ['All Markets']

          return params.data.market_ids?.map((market) => marketData.rows.find((m) => m.id === market)?.name)
        },
      },
      valueGetter: (params) => {
        if (!params.data.market_ids?.length) return ['All Markets']
        return params.data.market_ids?.map((market) => marketData.rows.find((m) => m.id === market)?.name)
      },
    },
    {
      headerName: 'Password',
      menuTabs: [],
      sortable: false,
      enableRowGroup: false,
      minWidth: 180,
      editable: false,
      cellStyle: {
        padding: 0,
      },
      filter: false,
      cellRenderer: ({ data }) => {
        if (data.disabled) return null
        const isDriver = data.is_driver
        return isDriver ? (
          <>
            <Button onClick={() => setViewingPWModal(true)}>Change Password</Button>
            <Modal
              onCancel={() => setViewingPWModal(false)}
              destroyOnClose
              footer={null}
              title={<Texto>Change Driver Password</Texto>}
              visible={viewingPWModal}
            >
              <PWModalContent setViewingPWModal={setViewingPWModal} driver={data} />
            </Modal>
          </>
        ) : (
          <GraviButton
            className='mr-3'
            data-cy='reset-user-pw'
            appearance='filled'
            onClick={() => sendResetEmail(data)}
            icon={<LockOutlined />}
          >
            Reset Password
          </GraviButton>
        )
      },
    },
  ]
}

export const CustomSelectEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState(props?.data?.market_ids || [])
  const refInput = useRef(null)
  useImperativeHandle(ref, () => {
    return {
      getValue() {
        return value
      },
      isPopup() {
        return true
      },
      isCancelBeforeStart() {
        return false
      },
      isCancelAfterEnd() {
        return false
      },
    }
  })
  const handleFilterOption = (input, option) => {
    return (option?.label ? option.label.toLowerCase() : '').includes(input?.toLowerCase())
  }

  return (
    <Select
      showSearch
      style={{ minWidth: 240 }}
      value={value}
      onChange={(v) => {
        if (v?.length > 1 && v.includes('')) {
          if (v?.[0] === '') {
            setValue([v?.[1]])
          } else {
            setValue([''])
          }
        } else {
          setValue(v)
        }
      }}
      mode={'multiple'}
      allowClear
      defaultOpen
      ref={refInput}
      autoFocus
      menuItemSelectedIcon={null}
      listHeight={400}
      options={props.options || []}
      placeholder='Select Markets'
      filterOption={handleFilterOption}
      optionLabelProp={'label'}
      onClear={() => setValue('')}
    />
  )
})
